import { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import PdpSharedCustomsMessage from './PdpSharedCustomsMessage';

//components
import { FormattedPrice } from 'dibs-buyer-price';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { shippingMethodDescriptions } from '../messages/pdpShippingInfoMessages';

import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';

import styles from './PdpSharedShippingInfo.scss';

const SHIPPING_TYPE = {
    PAID: 'paid',
    PAID_WITH_DISCOUNT: 'paid_discounted',
    FREE_SHIPPING: 'free',
    FREE_SHIPPING_WITH_DISCOUNT: 'free_discounted',
};

export const AVAILABLE_NOW = 'available-now';

export const REQUEST_STATUS = {
    REQUEST_QUOTE: 'requestQuote',
    CUSTOM_PROVIDED: 'customQuoteProvided',
    CUSTOM_REQUESTED: 'customQuoteRequested',
};

const getRegionName = ({
    quoteRequestStatus,
    addressBook,
    regionalInfo,
    customQuoteShippingAddress,
}) => {
    const { personalAddress, proAddress, addressList } = addressBook || {};
    const { displayCountry, zipCode } = proAddress || personalAddress || addressList?.[0] || {};
    const { countryName, zipCode: regionalZipCode } = regionalInfo?.[0] || {};

    if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_PROVIDED) {
        return customQuoteShippingAddress;
    }

    if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED) {
        return customQuoteShippingAddress;
    }

    return `${displayCountry || countryName || ''} ${zipCode || regionalZipCode || ''}`;
};

const getShippingType = ({ price, fullPrice, showShippingDiscounts }) => {
    const isFreeShipping = price === 0 && showShippingDiscounts;
    const isDiscountedShipping = fullPrice !== 0 && fullPrice > price && showShippingDiscounts;

    if (isFreeShipping && isDiscountedShipping) {
        return SHIPPING_TYPE.FREE_SHIPPING_WITH_DISCOUNT;
    }

    if (isFreeShipping) {
        return SHIPPING_TYPE.FREE_SHIPPING;
    }

    if (isDiscountedShipping) {
        return SHIPPING_TYPE.PAID_WITH_DISCOUNT;
    }

    return SHIPPING_TYPE.PAID;
};

const getShippingTypeMessage = ({ price, fullPrice, currency, showShippingDiscounts }) => {
    return (
        <FormattedMessage
            id="pdp.PdpSharedShippingInfoComponent.shippingType"
            defaultMessage="{shippingType, select,
                free {<Wrapper>Free Shipping:</Wrapper>}
                free_discounted {<Wrapper>Free Shipping:</Wrapper> {discountPrice}}
                paid {{shippingPrice}}
                paid_discounted {{discountPrice} {styledShippingPrice}}
                other {}
            }"
            values={{
                shippingType: getShippingType({
                    price,
                    fullPrice,
                    showShippingDiscounts,
                }),
                discountPrice: (
                    <span className={styles.discountedPrice}>
                        <FormattedPrice currency={currency} value={fullPrice} />
                    </span>
                ),
                shippingPrice: (
                    <span>
                        <FormattedPrice currency={currency} value={price} />
                    </span>
                ),
                styledShippingPrice: (
                    <span className={styles.salePrice}>
                        <FormattedPrice currency={currency} value={price} />
                    </span>
                ),
                Wrapper: message => <span className={styles.freeShippingMessage}>{message}</span>,
            }}
        />
    );
};

export const PdpSharedShippingInfoComponent = ({
    viewer,
    item,
    user,
    currency,
    children,
    shipmentQuote,
    shipment,
    quoteRequestStatus,
    leadTime,
    renderTooltip,
    showCustomsInfo,
    isClient,
}) => {
    const [tooltipHover, setTooltipHover] = useState(false);
    const { showShippingDiscounts = false } = viewer || {};
    const {
        subTotalAmount,
        buyerServiceMethodDisplay: shippingMethodDisplay,
        handlingTimeDisplay,
        showQuoteAsPending,
    } = shipmentQuote || {};
    const customQuoteShippingAddress = shipment?.shippingAddress?.displayCityStateCountry;

    const price = useShippingPrice({
        viewer,
        item,
        currency,
        type: FREE_SHIPPING_AND_COST,
    });

    const fullPrice =
        subTotalAmount?.convertedAmountList?.find(p => p?.currency === currency)?.amount || 0;

    let overviewMessage;

    if (!shipmentQuote || quoteRequestStatus === REQUEST_STATUS.REQUEST_QUOTE) {
        overviewMessage = (
            <FormattedMessage
                id="pdp.PdpSharedBlurbShippingCustomQuote.overviewRequestQuote"
                defaultMessage="Rates vary by destination and complexity."
            />
        );
    } else if (quoteRequestStatus === REQUEST_STATUS.CUSTOM_REQUESTED || showQuoteAsPending) {
        overviewMessage = (
            <span data-tn="quote-requested">
                <FormattedMessage
                    id="pdp.PdpSharedBlurbShippingCustomQuote.overviewPendingDetails"
                    defaultMessage="Quote pending to {customQuoteShippingAddress}"
                    values={{ customQuoteShippingAddress }}
                />
            </span>
        );
    } else {
        const { serviceType, serviceMethodType } = shipmentQuote?.serviceMethod || {};
        const shippingMethodDescription =
            shippingMethodDescriptions?.[serviceType]?.[serviceMethodType];

        overviewMessage = (
            <span data-tn="prequote-overview">
                <FormattedMessage
                    id="pdp.PdpSharedShippingInfo.prequoteOverview"
                    defaultMessage="{shippingType} <tooltip>{method} Shipping</tooltip> to {region}, arrives in {handlingTime}{isAvailable, select, 
                        true {.} 
                        other {, excluding production time.}
                    }"
                    values={{
                        shippingType: getShippingTypeMessage({
                            price,
                            fullPrice,
                            currency,
                            showShippingDiscounts,
                        }),
                        method: shippingMethodDisplay,
                        region: getRegionName({
                            quoteRequestStatus,
                            customQuoteShippingAddress,
                            regionalInfo: viewer?.regionalInfo,
                            addressBook: user?.addressBook,
                        }),
                        handlingTime: handlingTimeDisplay,
                        isAvailable: leadTime === AVAILABLE_NOW,
                        tooltip: content => (
                            <span
                                className={styles.hoverable}
                                onMouseEnter={() => setTooltipHover(true)}
                                onMouseLeave={() => setTooltipHover(false)}
                            >
                                {content}
                                {renderTooltip && shippingMethodDescription && (
                                    <Tooltip isVisible={tooltipHover} direction="bottom">
                                        {shippingMethodDescription}
                                    </Tooltip>
                                )}
                            </span>
                        ),
                    }}
                />
            </span>
        );
    }

    return (
        <span className={styles.shippingInfoContainer}>
            <PdpSharedPromoCode
                onlyShippingPromos
                excludeShippingPromos={price === 0}
                itemId={item?.serviceId}
                viewer={viewer}
                user={user}
                userId={user?.serviceId}
                displayInfo={null}
                currency={currency}
            />
            {overviewMessage} {children}
            {showCustomsInfo && <PdpSharedCustomsMessage item={item} isClient={isClient} />}
        </span>
    );
};

PdpSharedShippingInfoComponent.propTypes = {
    viewer: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    currency: PropTypes.string.isRequired,
    quoteRequestStatus: PropTypes.string,
    children: PropTypes.node,
    leadTime: PropTypes.string,
    renderTooltip: PropTypes.bool,
    user: PropTypes.object,
    showCustomsInfo: PropTypes.bool,
    isClient: PropTypes.bool,
};

export const PdpSharedShippingInfo = createFragmentContainer(PdpSharedShippingInfoComponent, {
    viewer: graphql`
        fragment PdpSharedShippingInfo_viewer on Viewer {
            showShippingDiscounts: featureFlag(feature: "subsidizedShippingPDP")
            regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode) {
                countryName
                zipCode
            }
            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...PdpSharedPromoCode_viewer
        }
    `,
    item: graphql`
        fragment PdpSharedShippingInfo_item on Item {
            serviceId
            ...PdpSharedCustomsMessage_item
            ...useShippingPrice_item @arguments(buyerId: $userId, fetchShippingCostInfo: true)
        }
    `,
    shipment: graphql`
        fragment PdpSharedShippingInfo_shipment on Shipment {
            shippingAddress {
                displayCityStateCountry
            }
        }
    `,
    shipmentQuote: graphql`
        fragment PdpSharedShippingInfo_shipmentQuote on ShipmentQuote {
            handlingTimeDisplay(displayShort: true)
            subTotalAmount {
                convertedAmountList {
                    currency
                    amount
                }
            }
            serviceMethod {
                serviceType
                serviceMethodType
            }
            buyerServiceMethodDisplay
            showQuoteAsPending
        }
    `,
    user: graphql`
        fragment PdpSharedShippingInfo_user on User {
            serviceId
            addressBook {
                proAddress {
                    displayCountry
                    zipCode
                }
                personalAddress {
                    displayCountry
                    zipCode
                }
                addressList {
                    displayCountry
                    zipCode
                }
            }
            ...PdpSharedPromoCode_user
        }
    `,
});
